<template>
  <div class="section-container">
    <div class="section-header">
      <h3 class="section-title">Course Enrollment for {{ student.name }}</h3>
      <button class="button button-primary" @click="showEnrollmentModal = true">
        + Add Course
      </button>
    </div>

    <div v-if="enrollments.length > 0">
      <table class="data-table">
        <thead>
          <tr>
            <th>Course ID</th>
            <th>Name</th>
            <th>Subject</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="enrollment in enrollments" :key="enrollment.course_id">
            <td>{{ enrollment.course.course_id }}</td>
            <td>{{ enrollment.course.name }}</td>
            <td>{{ enrollment.course.subject }}</td>
            <td>{{ enrollment.status || 'Active' }}</td>
            <td>
              <button class="button button-outline" @click="openEditModal(enrollment)">
                Edit
              </button>
              <button class="button button-outline" @click="confirmDelete(enrollment.course_id)">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="empty-state">
      No courses enrolled yet
    </div>

    <CourseStudentModal
      v-if="showEnrollmentModal || currentEnrollment"
      :studentId="student.student_id"
      :enrollment="currentEnrollment"
      :availableCourses="availableCourses"
      @save="handleSave"
      @close="closeModal"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery, useMutation } from '@vue/apollo-composable';
import CourseStudentModal from './CourseStudentModal.vue';
import { GET_COURSES_FOR_STUDENT, GET_COURSES, DELETE_STUDENT_COURSE } from '../graphql/queries';

const props = defineProps({
  student: {
    type: Object,
    required: true
  }
})

const showEnrollmentModal = ref(false)
const currentEnrollment = ref(null)

// Fetch enrollments
const { result: enrollmentResult, refetch: refetchEnrollments } = useQuery(
  GET_COURSES_FOR_STUDENT,
  () => ({ student_id: props.student.student_id }),
  { 
    enabled: computed(() => !!props.student.student_id),
    fetchPolicy: 'network-only',
  }
);

// Fetch courses not yet enrolled
const { result: coursesResult } = useQuery(GET_COURSES);

const enrollments = computed(() => enrollmentResult.value?.coursesForStudent || [])
const availableCourses = computed(() => coursesResult.value?.courses || [])

// Delete enrollment
const { mutate: deleteEnrollment } = useMutation(DELETE_STUDENT_COURSE);
const confirmDelete = (courseId) => {
  if (confirm('Are you sure you want to remove this student from the course?')) {
    deleteEnrollment({
      student_id: props.student.student_id,
      course_id: courseId
    }).then(() => refetchEnrollments());
  }
};

// Modal handlers
const openEditModal = (enrollment) => {
  currentEnrollment.value = enrollment
}

const closeModal = () => {
  showEnrollmentModal.value = false
  currentEnrollment.value = null
}

const handleSave = () => {
  closeModal()
  refetchEnrollments()
}
</script>

<style scoped>
/* Same styles as StudentCourseList.vue */
.enrollment-container {
  margin-top: 2rem;
  border-top: 1px solid #eee;
  padding-top: 1rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

th, td {
  padding: 0.75rem;
  border: 1px solid #ddd;
  text-align: left;
}

th {
  background-color: #f5f5f5;
}

button {
  margin-right: 0.5rem;
  padding: 0.375rem 0.75rem;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

button:hover {
  background-color: #e0e0e0;
}

.no-enrollments {
  padding: 1rem;
  background-color: #f9f9f9;
  border: 1px dashed #ddd;
  text-align: center;
  color: #666;
}
</style>